import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Truck,
  Search,
  Link as LinkIcon,
  Copy as CopyIcon,
  PackageCheck,
  PackageOpen,
  CheckCircle2,
  History,
  Filter,
  Download,
  Tag,
  AlertTriangle,
  Package,
  ChevronRight,
  ChevronDown,
  FileText,
} from "lucide-react";
import BusinessApiService from "../../services/BusinessApiService";
import { getCurrentBusinessId } from "../../services/api";
import toast, { Toaster } from "react-hot-toast";
import { utils as xlsxUtils, writeFile as xlsxWriteFile } from "xlsx";

// ---------- Types ----------
type Product = {
  productId: string;
  name: string;
  sku?: string;
  totalTags?: number;
  shippedTags?: number;
};
type StoreT = {
  storeId: string;
  storeName: string;
  city?: string;
  state?: string;
  country?: string;
  isActive?: boolean;
  latitude?: number;
  longitude?: number;
  address?: any;
};
type ShipmentStatus =
  | "created"
  | "shipped"
  | "in_transit"
  | "delivered"
  | "received"
  | "exception"
  | "returned";
type StatusHistory = { toStatus: string; reason?: string; changedAt?: string };

type ShipmentFile = {
  url: string;
  type?: string;
  description?: string;
  mimeType?: string;
  uploadedAt?: string;
  fileNumber?: number; // Unique number for this file
};

type Shipment = {
  shipmentId: string;
  batchId: string;
  carrier: string;
  trackingNumber?: string;
  storeId: string;
  serviceLevel?: string;
  status?: ShipmentStatus;
  files?: ShipmentFile[];
  updatedAt?: string;
  statusHistory?: StatusHistory[];
  product?: Product | null;
  tagCount?: number;
};

// ---------- Helpers ----------
const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const getAuthToken = () =>
  BusinessApiService.getBusinessToken?.() ||
  localStorage.getItem("authToken") ||
  localStorage.getItem("businessToken") ||
  localStorage.getItem("token") ||
  "";
const authHeader = () => ({ Authorization: `Bearer ${getAuthToken()}` });

const carrierLink = (carrier?: string, tracking?: string): string | undefined => {
  if (!carrier || !tracking) return undefined;
  const c = carrier.toLowerCase();
  if (c.includes("fedex")) return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(tracking)}`;
  if (c.includes("ups")) return `https://www.ups.com/track?tracknum=${encodeURIComponent(tracking)}`;
  if (c.includes("dhl"))
    return `https://www.dhl.com/global-en/home/tracking.html?tracking-id=${encodeURIComponent(tracking)}`;
  if (c.includes("usps"))
    return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(tracking)}`;
  return undefined;
};

const reqJSON = async (url: string, init?: RequestInit) => {
  const r = await fetch(url, init);
  let j: any = {};
  try {
    j = await r.json();
  } catch {}
  if (!r.ok || j?.success === false) {
    const msg = j?.message || j?.error || `HTTP ${r.status}`;
    throw new Error(msg);
  }
  return j;
};

const statusColor = (s?: string) => {
  switch ((s || "").toLowerCase()) {
    case "shipped":
      return "bg-amber-100 text-amber-800";
    case "in_transit":
      return "bg-blue-100 text-blue-800";
    case "delivered":
      return "bg-emerald-100 text-emerald-800";
    case "received":
      return "bg-green-100 text-green-800";
    case "exception":
      return "bg-red-100 text-red-800";
    case "returned":
      return "bg-gray-200 text-gray-700";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const statusDot = (s?: string) => {
  switch ((s || "").toLowerCase()) {
    case "shipped":
      return "bg-amber-500";
    case "in_transit":
      return "bg-blue-500";
    case "delivered":
      return "bg-emerald-500";
    case "received":
      return "bg-green-500";
    case "exception":
      return "bg-red-500";
    case "returned":
      return "bg-gray-500";
    default:
      return "bg-gray-400";
  }
};

const SUPPLY_CHAIN_STEPS: { status: ShipmentStatus; label: string }[] = [
  { status: "shipped", label: "Ready to Ship" },
  { status: "in_transit", label: "In Transit" },
  { status: "delivered", label: "Delivered" },
  { status: "received", label: "Received" },
];

// ---------- Main Component ----------
const SupplyChainPage = () => {
  // Data states
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [batches, setBatches] = useState<Product[]>([]);
  const [stores, setStores] = useState<StoreT[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Filters/UI states
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | ShipmentStatus | "no_tracking">("all");
  const [storeFilter, setStoreFilter] = useState<string>("all");
  const [productFilter, setProductFilter] = useState<string>("all");

  // Shipment creation form
  const [batchId, setBatchId] = useState("");
  const [carrierSelect, setCarrierSelect] = useState("FedEx");
  const [carrierCustom, setCarrierCustom] = useState("");
  const [storeId, setStoreId] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [serviceLevel, setServiceLevel] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Files for new shipment
  const [shipmentFiles, setShipmentFiles] = useState<File[]>([]);
  const [shipmentFileType, setShipmentFileType] = useState("package_photo");
  const [shipmentFileDesc, setShipmentFileDesc] = useState("");
  const [shipmentFileUploading, setShipmentFileUploading] = useState(false);

  // Receipt confirmation & details modal
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null);
  const [receiptNotes, setReceiptNotes] = useState("");
  const [view, setView] = useState<Shipment | null>(null);

  // ---------- Loaders ----------
  const loadShipments = useCallback(async () => {
    try {
      setError(null);
      const businessId = getCurrentBusinessId();
      if (!businessId) throw new Error("No business ID");
      const r = await fetch(
        `${API_BASE}/shipments?businessId=${encodeURIComponent(businessId)}`,
        {
          headers: { ...authHeader(), "Content-Type": "application/json" },
        }
      );
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error || j?.message || `HTTP ${r.status}`);
      setShipments(j.data?.shipments || []);
    } catch (e: any) {
      setError(e?.message || "Failed to load shipments");
      setShipments([]);
    }
  }, []);
  const loadBatches = useCallback(async () => {
    try {
      const businessId = getCurrentBusinessId();
      if (!businessId) throw new Error("No business ID");
      const r = await fetch(`${API_BASE}/batches?businessId=${encodeURIComponent(businessId)}`, {
        headers: { ...authHeader(), "Content-Type": "application/json" },
      });
      const j = await r.json();
      const rows = j.data?.batches || j.batches || j.data || [];
      setBatches(
        rows.map((b: any) => ({
          productId: b.batchId || b._id || String(b.id || ""),
          name: b.name || b.batchNumber || "",
          sku: b.product?.sku,
          totalTags: b.tags?.length || 0,
        }))
      );
    } catch {
      setBatches([]);
    }
  }, []);
  const loadStores = useCallback(async () => {
    try {
      const businessId = getCurrentBusinessId();
      if (!businessId) return setStores([]);
      const r = await fetch(`${API_BASE}/stores?businessId=${encodeURIComponent(businessId)}`, {
        headers: { ...authHeader(), "Content-Type": "application/json" },
      });
      const j = await r.json();
      const rows = j.data?.stores || j.stores || j.data || [];
      setStores(
        rows.map((s: any) => ({
          storeId: s.storeId || s._id || String(s.id || ""),
          storeName: s.storeName || s.name || "Unknown Store",
          city: s.city,
          state: s.state,
          country: s.country,
          isActive: s.isActive !== false,
          latitude: s.latitude,
          longitude: s.longitude,
          address: s.address,
        }))
      );
    } catch {
      setStores([]);
    }
  }, []);
  const loadProducts = useCallback(async () => {
    try {
      const businessId = getCurrentBusinessId();
      if (!businessId) return setProducts([]);
      const r = await fetch(`${API_BASE}/products?businessId=${encodeURIComponent(businessId)}`, {
        headers: { ...authHeader(), "Content-Type": "application/json" },
      });
      const j = await r.json();
      const rows = j.data?.products || j.products || j.data || [];
      setProducts(
        rows.map((p: any) => ({
          productId: p.productId || p._id || String(p.id || ""),
          name: p.name || p.productName || "Unknown Product",
          sku: p.sku,
          totalTags: p.totalTags || 0,
          shippedTags: p.shippedTags || 0,
        }))
      );
    } catch {
      setProducts([]);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadShipments(), loadBatches(), loadStores(), loadProducts()]);
      setLoading(false);
    })();
  }, [refreshKey, loadShipments, loadBatches, loadStores, loadProducts]);

  // ---------- Actions ----------
  const MAX_FILE_MB = 20;
  const validateFiles = (files: File[]) => {
    for (const f of files) {
      const okType = f.type.startsWith("image/") || f.type === "application/pdf";
      const okSize = f.size <= MAX_FILE_MB * 1024 * 1024;
      if (!okType) throw new Error(`Unsupported file type: ${f.name}`);
      if (!okSize) throw new Error(`File too large (> ${MAX_FILE_MB}MB): ${f.name}`);
    }
  };

 const createShipment = async (): Promise<string | null> => {
  const carrierName = (carrierSelect === "Other" ? carrierCustom : carrierSelect).trim();
  const businessId = getCurrentBusinessId(); // Get the business ID from your session or context

  // Check for required fields, now including businessId
  if (!batchId || !carrierName || !storeId || !businessId) {
    toast.error("Fill all required fields");
    return null;
  }

  // Generate a unique shipmentId (if your backend expects the client to do this)
  const shipmentId = `SHIP-${Date.now()}-${Math.floor(Math.random() * 10000)}`;

  // Build the payload including required fields
  const payload = {
    businessId,
    shipmentId,
    batchId,
    carrier: carrierName,
    storeId,
    trackingNumber: trackingNumber?.trim() || undefined,
    serviceLevel: serviceLevel?.trim() || undefined,
  };

  // Send the request
  const r = await fetch(`${API_BASE}/shipments`, {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const j = await r.json();
  if (!r.ok || !j.success) {
    toast.error(j?.message || j?.error || `Failed to create shipment`);
    return null;
  }
  toast.success("Shipment created!");
  return j.data?.shipment?.shipmentId || shipmentId;
};
  const uploadShipmentFile = async (
    shipmentId: string,
    file: File,
    type: string,
    description: string,
    fileNumber?: number
  ) => {
    const formData = new FormData();
    formData.append("shipmentId", shipmentId);
    formData.append("file", file);
    formData.append("type", type);
    if (description) formData.append("description", description);
    if (fileNumber !== undefined) formData.append("fileNumber", String(fileNumber));

    const r = await fetch(`${API_BASE}/shipments/upload-file`, {
      method: "POST",
      headers: { ...authHeader() }, // do NOT set Content-Type for multipart
      body: formData,
    });
    if (!r.ok) {
      let msg = `Upload failed (${r.status})`;
      try {
        const j = await r.json();
        msg = j?.message || msg;
      } catch {}
      throw new Error(msg);
    }
  };
const updateShipmentStatus = async (shipmentId: string, newStatus: ShipmentStatus, reason?: string) => {
  try {
    const r = await fetch(`${API_BASE}/shipments/${shipmentId}/status`, {
      method: "POST",
      headers: { ...authHeader(), "Content-Type": "application/json" },
      body: JSON.stringify({ status: newStatus, reason }),
    });
    const j = await r.json();
    if (!r.ok || !j.success) throw new Error(j?.error || j?.message || `Failed to update status`);
    toast.success(`Shipment marked as ${newStatus.replace(/_/g, " ")}`);
    setRefreshKey(k => k + 1); // reload shipments
  } catch (e: any) {
    toast.error(e?.message || "Status update failed");
  }
};
  const shipSelectedBatch = async (): Promise<void> => {
    try { validateFiles(shipmentFiles); }
    catch (e: any) { toast.error(e?.message || "Invalid files"); return; }

    setSubmitting(true);
    try {
      // 1. Create shipment
      const shipmentId = await createShipment();
      if (!shipmentId) return;

      // 2. Upload files to shipment
      if (shipmentFiles.length > 0) {
        setShipmentFileUploading(true);
        for (let i = 0; i < shipmentFiles.length; i++) {
          const f = shipmentFiles[i];
          await uploadShipmentFile(shipmentId, f, shipmentFileType, shipmentFileDesc, i + 1);
        }
        setShipmentFileUploading(false);
      }

      toast.success("Shipment created and files uploaded!");

      // Reset form
      setBatchId(""); setCarrierSelect("FedEx"); setCarrierCustom("");
      setStoreId(""); setTrackingNumber(""); setServiceLevel("");
      setShipmentFiles([]); setShipmentFileDesc("");
      setRefreshKey(k => k + 1);
    } catch (e: any) {
      toast.error(`Shipment failed: ${e.message || e.toString()}`);
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Derived ----------
  const enriched = useMemo(
    () =>
      shipments.map((s) => ({
        ...s,
        store: stores.find((t) => t.storeId === s.storeId),
        product: products.find((p) => p.productId === s.batchId),
      })),
    [shipments, stores, products]
  );

  const filteredShipments = useMemo(() => {
    const s = q.trim().toLowerCase();
    return enriched.filter((b: any) => {
      const matchQ =
        !s ||
        (b.product?.name || "").toLowerCase().includes(s) ||
        (b.trackingNumber || "").toLowerCase().includes(s) ||
        (b.store?.storeName || "").toLowerCase().includes(s);

      const matchStatus =
        statusFilter === "all"
          ? true
          : statusFilter === "no_tracking"
          ? !b.trackingNumber
          : (b.status || "").toLowerCase() === statusFilter;

      const matchStore = storeFilter === "all" ? true : b.storeId === storeFilter;
      const matchProduct = productFilter === "all" ? true : b.batchId === productFilter;
      return matchQ && matchStatus && matchStore && matchProduct;
    });
  }, [enriched, q, statusFilter, storeFilter, productFilter]);

  const getStepIndex = (status?: string) => {
    const idx = SUPPLY_CHAIN_STEPS.findIndex((s) => s.status === status);
    return idx >= 0 ? idx : -1;
  };

  const openTracking = (carrier?: string, tn?: string, fallbackUrl?: string) => {
    const url = fallbackUrl || carrierLink(carrier, tn);
    if (url) window.open(url, "_blank");
  };

  const copyText = async (text?: string) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {}
  };

  const exportCSV = () => {
    const data = shipments.map((s) => ({
      Shipment: s.shipmentId,
      Batch: s.batchId,
      Product: s.product?.name,
      Carrier: s.carrier,
      Tracking: s.trackingNumber,
      Store: stores.find((t) => t.storeId === s.storeId)?.storeName,
      Status: s.status,
      Updated: s.updatedAt,
    }));
    const ws = xlsxUtils.json_to_sheet(data);
    const wb = xlsxUtils.book_new();
    xlsxUtils.book_append_sheet(wb, ws, "Shipments");
    xlsxWriteFile(wb, "shipments.xlsx");
    toast.success("Shipments exported as Excel!");
  };

  // ---------- UI ----------
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-2 text-gray-600">Loading Supply Chain…</p>
        </div>
        <Toaster position="top-right" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />

      {/* Step Progress Bar */}
      <div className="bg-white rounded-xl shadow-sm border p-4 mb-2 flex flex-col gap-3">
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Supply Chain Workflow</h2>
        <div className="flex items-center gap-3 flex-wrap">
          {SUPPLY_CHAIN_STEPS.map((step, i) => (
            <div key={step.status} className="flex items-center gap-2">
              <div
                className={`rounded-full px-3 py-1 text-sm font-medium transition ${
                  shipments.some((s) => s.status === step.status)
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-500"
                }`}
              >
                {step.label}
              </div>
              {i < SUPPLY_CHAIN_STEPS.length - 1 && <ChevronRight className="text-gray-400" size={20} />}
            </div>
          ))}
        </div>
        <div className="text-sm text-gray-500 mt-2">
          <b>How to process:</b> 1. Ship batch → 2. Mark In Transit → 3. Mark Delivered → 4. Confirm Receipt.
        </div>
        {error && <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">⚠️ {error}</div>}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border p-4 flex flex-col md:flex-row md:items-end gap-3">
        <div className="relative md:w-72">
          <label className="text-sm text-gray-700">Search</label>
          <Search className="absolute right-3 bottom-3 text-gray-400" size={16} />
          <input
            className="mt-1 w-full border rounded px-3 py-2"
            placeholder="Batch, tracking, store, or product"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div>
          <label className="text-sm text-gray-700">Status</label>
          <select
            className="mt-1 w-full border rounded px-3 py-2"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
          >
            <option value="all">All Status</option>
            <option value="no_tracking">No Tracking</option>
            <option value="created">Created</option>
            <option value="shipped">Ready to Ship</option>
            <option value="in_transit">In Transit</option>
            <option value="delivered">Delivered</option>
            <option value="received">Received</option>
            <option value="exception">Exception</option>
            <option value="returned">Returned</option>
          </select>
        </div>
        <div>
          <label className="text-sm text-gray-700">Product</label>
          <select
            className="mt-1 w-full border rounded px-3 py-2"
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
          >
            <option value="all">All Products</option>
            {products.map((p) => (
              <option key={p.productId} value={p.productId}>
                {p.name} {p.sku ? `(${p.sku})` : ""}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm text-gray-700">Store</label>
          <select
            className="mt-1 w-full border rounded px-3 py-2"
            value={storeFilter}
            onChange={(e) => setStoreFilter(e.target.value)}
          >
            <option value="all">All Stores</option>
            {stores.map((s) => (
              <option key={String(s.storeId||"")} value={String(s.storeId||"")}>
                {s.storeName} {s.city ? ` (${s.city})` : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="ml-auto flex items-center gap-2 text-sm text-gray-500">
          <Filter size={14} /> {filteredShipments.length} shown
        </div>
        <div>
          <button
            onClick={exportCSV}
            className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
          >
            <Download size={16} /> Export Shipments
          </button>
        </div>
      </div>

      {/* Ship a Batch Form */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Truck size={18} /> Create Shipment
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="text-sm text-gray-700">Batch *</label>
            <select
              value={batchId}
              onChange={(e) => setBatchId(e.target.value)}
              className="mt-1 w-full border rounded px-3 py-2"
            >
              <option value="">Choose Batch…</option>
              {batches.map((b) => (
                <option key={b.productId} value={b.productId}>
                  {b.name} {b.sku ? `(${b.sku})` : ""} ({b.totalTags || 0} tags)
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-700">Carrier *</label>
            <select
              value={carrierSelect}
              onChange={(e) => setCarrierSelect(e.target.value)}
              className="mt-1 w-full border rounded px-3 py-2"
            >
              {["FedEx", "UPS", "DHL", "USPS", "Other"].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          {carrierSelect === "Other" && (
            <div>
              <label className="text-sm text-gray-700">Carrier Name</label>
              <input
                value={carrierCustom}
                onChange={(e) => setCarrierCustom(e.target.value)}
                className="mt-1 w-full border rounded px-3 py-2"
                placeholder="Enter carrier company"
              />
            </div>
          )}
          <div>
            <label className="text-sm text-gray-700">Destination Store *</label>
            <select
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              className="mt-1 w-full border rounded px-3 py-2"
            >
              <option value="">Choose Store…</option>
              {stores
                .filter((s) => s.isActive !== false)
                .map((s) => (
                  <option key={String(s.storeId||"")} value={String(s.storeId||"")}>
                    {s.storeName}
                    {s.city ? ` (${s.city})` : ""}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-700">Tracking # (auto-gen if blank)</label>
            <input
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              className="mt-1 w-full border rounded px-3 py-2"
              placeholder="Optional"
            />
          </div>
          <div>
            <label className="text-sm text-gray-700">Service Level</label>
            <input
              value={serviceLevel}
              onChange={(e) => setServiceLevel(e.target.value)}
              className="mt-1 w-full border rounded px-3 py-2"
              placeholder="Ground, Priority…"
            />
          </div>
        </div>

        {/* --- Shipment file upload UI --- */}
        <div className="flex flex-col md:flex-row gap-3 items-end mt-4">
          <div className="flex-1">
            <label className="text-sm text-gray-700">Attach Images/PDFs (shipment)</label>
            <input
              type="file"
              accept="image/*,.pdf"
              multiple
              onChange={(e) => setShipmentFiles(e.target.files ? Array.from(e.target.files) : [])}
              disabled={shipmentFileUploading || submitting}
              className="mt-1 w-full border rounded px-3 py-2"
            />
            {shipmentFiles.length > 0 && (
              <div className="mt-2 text-xs text-gray-500">{shipmentFiles.length} file(s) selected</div>
            )}
          </div>
          <div>
            <label className="text-sm text-gray-700">File Type</label>
            <select
              value={shipmentFileType}
              onChange={(e) => setShipmentFileType(e.target.value)}
              className="mt-1 border rounded px-3 py-2"
            >
              <option value="package_photo">Package Photo</option>
              <option value="invoice">Invoice</option>
              <option value="customs">Customs Document</option>
              <option value="delivery">Delivery Photo</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="text-sm text-gray-700">File Description</label>
            <input
              placeholder="e.g., Outer box, Packing slip, etc."
              value={shipmentFileDesc}
              onChange={(e) => setShipmentFileDesc(e.target.value)}
              className="mt-1 w-full border rounded px-3 py-2"
            />
          </div>
        </div>

        <div className="flex justify-end mt-3">
          <button
            disabled={submitting || shipmentFileUploading}
            onClick={shipSelectedBatch}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Truck size={16} /> {submitting ? "Processing…" : "Ship Batch & Upload Files"}
          </button>
        </div>
      </div>

      {/* Shipments List */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b text-sm font-semibold text-gray-700 flex items-center gap-2">
          <Package size={16} />
          Shipments ({filteredShipments.length})
        </div>
        <div className="divide-y">
          {filteredShipments.length === 0 && (
            <div className="p-6 text-sm text-gray-600">No matching shipments.</div>
          )}
          {filteredShipments.map((s) => {
            const dest = stores.find((store) => store.storeId === s.storeId);
            const product = s.product;
            const stepIdx = getStepIndex(s.status);

            return (
              <div key={String(s.shipmentId||"")} className="p-4 flex items-center justify-between hover:bg-gray-50">
                <div className="min-w-0 flex-1">
                  {/* Stepper for this shipment */}
                  <div className="flex items-center gap-1 mb-1">
                    {SUPPLY_CHAIN_STEPS.map((step, idx) => (
                      <div key={step.status} className="flex items-center gap-0.5">
                        <div
                          className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold transition ${
                            stepIdx >= idx ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-500"
                          }`}
                        >
                          {idx + 1}
                        </div>
                        {idx < SUPPLY_CHAIN_STEPS.length - 1 && (
                          <ChevronRight className="text-gray-300" size={14} />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Shipment Info */}
                  <div className="font-medium text-gray-900 flex items-center gap-2 mb-1">
                    {s.status === "delivered" || s.status === "received" ? (
                      <PackageCheck className="text-emerald-600" size={18} />
                    ) : (
                      <PackageOpen className="text-blue-600" size={18} />
                    )}
                    <span className="truncate">{product?.name}</span>
                    <span className="text-xs text-gray-500">{String(s.batchId||"")}</span>
                    <span
                      className={`ml-2 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${statusColor(
                        s.status
                      )}`}
                    >
                      {s.status || "created"}
                    </span>
                  </div>

                  {/* Product Info */}
                  {product && (
                    <div className="text-sm text-blue-600 mb-1 flex items-center gap-1">
                      <Tag size={14} />
                      <span className="font-medium">{product.name}</span>
                      {product.sku && <span className="text-gray-500">({product.sku})</span>}
                      {s.tagCount ? <span className="text-gray-600">• {s.tagCount} tags</span> : null}
                    </div>
                  )}

                  {/* Carrier, Store, Tracking, etc. */}
                  <div className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">{s.carrier || "No Carrier"}</span>
                    {s.trackingNumber && (
                      <>
                        <span className="mx-1">•</span>
                        <span className="font-mono">{s.trackingNumber}</span>
                      </>
                    )}
                    {dest && (
                      <>
                        <span className="mx-1">•</span>
                        <span>
                          to <span className="font-medium">{dest.storeName}</span>
                          {dest.city ? ` (${dest.city})` : ""}
                        </span>
                      </>
                    )}
                  </div>

                  <div className="text-xs text-gray-500 flex items-center gap-2">
                    <History size={12} />
                    Updated {s.updatedAt ? new Date(s.updatedAt).toLocaleString() : "—"}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 shrink-0">
                  {s.trackingNumber && (
                    <button
                      className="px-2 py-1 border rounded text-sm hover:bg-gray-100"
                      onClick={() => openTracking(s.carrier, s.trackingNumber)}
                      title="Open tracking"
                    >
                      <LinkIcon size={14} />
                    </button>
                  )}

                  <button
                    className="px-2 py-1 border rounded text-sm hover:bg-gray-100"
                    title="Copy tracking"
                    onClick={() => copyText(s.trackingNumber)}
                    disabled={!s.trackingNumber}
                  >
                    <CopyIcon size={14} />
                  </button>

                  <button className="px-2 py-1 border rounded text-sm hover:bg-gray-100" onClick={() => setView(s)}>
                    Details
                  </button>
 {/* <--- ADD STATUS DROPDOWN HERE ---> */}
  <select
    className="border rounded px-2 py-1 text-sm"
    value={s.status}
    onChange={(e) => updateShipmentStatus(s.shipmentId, e.target.value as ShipmentStatus)}
    style={{ minWidth: 120 }}
  >
    <option value={s.status}>{s.status || "created"}</option>
    {["in_transit", "delivered", "received", "exception", "returned"].filter(opt => opt !== s.status).map(opt => (
      <option key={opt} value={opt}>
        {opt.replace(/_/g, " ").replace(/^./, c => c.toUpperCase())}
      </option>
    ))}
  </select>


                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Details Modal */}
      {view && (
        <div className="fixed inset-0 bg-gray-600/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <CheckCircle2 className="text-blue-600" />
                Shipment Details — {view.product?.name}
              </h3>
              <button onClick={() => setView(null)} className="text-gray-400 hover:text-gray-600 text-2xl">
                ✕
              </button>
            </div>

            <div className="space-y-6 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-gray-500 font-medium">Shipment ID</div>
                  <div className="font-mono text-xs">{String(view.shipmentId||"")}</div>
                </div>
                <div>
                  <div className="text-gray-500 font-medium">Status</div>
                  <div className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${statusColor(view.status)}`}>
                    {view.status || "created"}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 font-medium">Product</div>
                  <div>{view.product?.name || "N/A"}</div>
                  {view.product?.sku && <div className="text-xs text-gray-600">SKU: {view.product.sku}</div>}
                </div>
                <div>
                  <div className="text-gray-500 font-medium">Tag Count</div>
                  <div className="flex items-center gap-2">
                    <Tag size={14} />
                    {view.tagCount || 0} total
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 font-medium">Carrier</div>
                  <div>{view.carrier || "—"}</div>
                </div>
                <div>
                  <div className="text-gray-500 font-medium">Tracking Number</div>
                  <div className="font-mono text-xs break-all">{view.trackingNumber || "—"}</div>
                </div>
                <div>
                  <div className="text-gray-500 font-medium">Destination Store</div>
                  <div>{stores.find((s) => String(s.storeId||"") === String(view.storeId||""))?.storeName || "—"}</div>
                </div>
                <div>
                  <div className="text-gray-500 font-medium">Last Updated</div>
                  <div>{view.updatedAt && typeof view.updatedAt === "string" ? new Date(view.updatedAt).toLocaleString() : "—"}</div>
                </div>
              </div>

              {/* --- FILES SECTION --- */}
              {view.files && view.files.length > 0 && (
                <div className="mt-4">
                  <div className="text-gray-700 font-bold mb-1">Shipment Files</div>
                  <ul className="space-y-2">
                    {view.files.map((f, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        {f.mimeType?.startsWith("image/") ? (
                          <a href={f.url} target="_blank" rel="noopener noreferrer">
                            <img
                              src={f.url}
                              alt={f.description || "Shipment Image"}
                              className="w-12 h-12 object-contain border rounded"
                            />
                          </a>
                        ) : f.mimeType === "application/pdf" ? (
                          <a
                            href={f.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-700 underline flex items-center gap-1"
                          >
                            <FileText size={16} /> PDF File {f.description && <>({f.description})</>}
                          </a>
                        ) : (
                          <a
                            href={f.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-700 underline flex items-center gap-1"
                          >
                            <Download size={16} /> {f.description || f.url}
                          </a>
                        )}
                        <span className="text-xs text-gray-400">
                          {f.uploadedAt ? new Date(f.uploadedAt).toLocaleString() : ""}
                          {f.fileNumber !== undefined ? <span className="ml-2">File #{f.fileNumber}</span> : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Status Timeline */}
              {view.statusHistory && view.statusHistory.length > 0 && (
                <div className="mt-4">
                  <div className="text-gray-700 font-bold mb-1">Shipment Status Timeline</div>
                  <ol className="list-none pl-0">
                    {view.statusHistory.map((h, idx) => (
                      <li key={idx} className="flex items-center gap-2 mb-2">
                        <div className={`w-3 h-3 rounded-full ${statusDot(h.toStatus)}`} />
                        <div className="font-semibold capitalize">{String(h.toStatus).replace(/_/g, " ")}</div>
                        <div className="text-xs text-gray-500">
                          {h.changedAt ? new Date(h.changedAt).toLocaleString() : ""}
                        </div>
                        {h.reason && <span className="italic text-gray-400">— {h.reason}</span>}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
              {view.trackingNumber && (
                <button
                  className="px-3 py-2 border rounded flex items-center gap-2 hover:bg-gray-50"
                  onClick={() => openTracking(view.carrier, view.trackingNumber)}
                >
                  <LinkIcon size={14} />
                  Open Tracking
                </button>
              )}
              <button
                className="px-3 py-2 border rounded flex items-center gap-2 hover:bg-gray-50"
                onClick={() => copyText(view.trackingNumber)}
                disabled={!view.trackingNumber}
              >
                <CopyIcon size={14} />
                Copy Tracking
              </button>
              <button className="px-3 py-2 bg-gray-700 text-white rounded" onClick={() => setView(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplyChainPage;